import { useEffect, useState } from "react";
import ActionButton from "@/components/ActionButton";
import api, { formatEur } from "@/lib/api";
import { Plus, Trash2, Edit2, Merge, BarChart3 } from "lucide-react";

const CATEGORIES = [
  { v: "conception", l: "Conception", c: "bg-amber-100 text-amber-800" },
  { v: "administratif", l: "Administratif", c: "bg-stone-200 text-stone-700" },
  { v: "commercial", l: "Commercial", c: "bg-emerald-100 text-emerald-800" },
  { v: "chantier", l: "Chantier", c: "bg-orange-100 text-orange-800" },
  { v: "franchise", l: "Franchise", c: "bg-rose-100 text-rose-800" },
  { v: "marketing", l: "Marketing", c: "bg-purple-100 text-purple-800" },
  { v: "franchimob", l: "Franchi'Mob", c: "bg-blue-100 text-blue-800" },
  { v: "autre", l: "Autre", c: "bg-stone-100 text-stone-600" },
];

const DEFAULT_COLORS = ["#C26B53", "#D17A6B", "#F4D7CC", "#EAC0AE", "#0F766E", "#0B5B55", "#A1503A", "#14161B", "#5B6770", "#92806E"];

export default function Prestations() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [mergeMode, setMergeMode] = useState(false);
  const [show, setShow] = useState(false);

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/prestations"), api.get("/prestations/stats")]);
    setList(a.data); setStats(b.data.items || []);
  };
  useEffect(() => { load(); }, []);

  const statsById = Object.fromEntries(stats.map((s) => [s.id, s]));

  const save = async (p, patch) => {
    await api.put(`/prestations/${p.id}`, patch);
    setEditing(null);
    load();
  };

  const create = async (e) => {
    e.preventDefault();
    const fd = Object.fromEntries(new FormData(e.target).entries());
    fd.taux_horaire = parseFloat(fd.taux_horaire || 0);
    await api.post("/prestations", fd);
    setShow(false);
    load();
  };

  const remove = async (p, force = false) => {
    try {
      await api.delete(`/prestations/${p.id}`, { params: force ? { force: true } : {} });
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        const n = e.response.data?.detail?.usage_count || "?";
        if (window.confirm(`${n} projet(s) utilisent « ${p.nom} ». Supprimer quand même ?`)) remove(p, true);
      }
    }
  };

  const toggleSel = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const doMerge = async () => {
    if (selected.size < 2) return alert("Sélectionnez au moins 2 prestations à fusionner.");
    const target_id = window.prompt("Saisissez l'ID exact de la prestation cible (parmi les sélectionnées) :", Array.from(selected)[0]);
    if (!target_id || !selected.has(target_id)) return;
    const sources = Array.from(selected).filter((id) => id !== target_id);
    const res = await api.post(`/prestations/${target_id}/merge`, { source_ids: sources });
    alert(`✅ ${res.data.merged} prestation(s) fusionnée(s), ${res.data.moved_projects} projet(s) re-rattaché(s).`);
    setSelected(new Set()); setMergeMode(false); load();
  };

  return (
    <div className="space-y-6" data-testid="prestations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-stone-500">Bibliothèque</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">Prestations</h1>
          <p className="text-sm text-stone-500 mt-1">{list.length} prestation(s) · catalogue auto-enrichi.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="prestation-merge-mode" onClick={() => setMergeMode((m) => !m)} className={`px-3 py-2 text-sm font-semibold rounded border ${mergeMode ? "bg-[var(--ppd)] text-white border-[var(--ppd)]" : "bg-white border-stone-200"}`}>
            <Merge size={14} className="inline -mt-0.5 mr-1"/> {mergeMode ? "Annuler fusion" : "Mode fusion"}
          </button>
          {mergeMode && selected.size >= 2 && (
            <button data-testid="prestation-do-merge" onClick={doMerge} className="bg-stone-900 text-white px-3 py-2 rounded text-sm font-semibold">Fusionner ({selected.size})</button>
          )}
          <button data-testid="prestation-new" onClick={() => setShow(true)} className="bg-stone-900 hover:bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2">
            <Plus size={16}/> Nouvelle
          </button>
        </div>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-wider text-stone-500 border-b">
            <tr>
              {mergeMode && <th className="px-3 py-3 w-10"></th>}
              <th className="px-4 py-3 text-left">Prestation</th>
              <th className="px-4 py-3 text-left">Catégorie</th>
              <th className="px-4 py-3 text-right">Taux H</th>
              <th className="px-4 py-3 text-right">Projets</th>
              <th className="px-4 py-3 text-right">Temps</th>
              <th className="px-4 py-3 text-right">CA réalisé</th>
              <th className="px-4 py-3 text-right">Marge</th>
              <th className="px-4 py-3 text-right w-24"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => {
              const s = statsById[p.id] || {};
              const isEd = editing === p.id;
              return (
                <tr key={p.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`prestation-row-${p.id}`}>
                  {mergeMode && (
                    <td className="px-3 py-3">
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`prestation-sel-${p.id}`}/>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: p.couleur }}/>
                      {isEd ? (
                        <input defaultValue={p.nom} onBlur={(e) => save(p, { nom: e.target.value })} autoFocus className="border border-stone-300 rounded px-2 py-1 text-sm" data-testid={`prestation-nom-input-${p.id}`}/>
                      ) : (
                        <span className="font-semibold">{p.nom}</span>
                      )}
                      <code className="text-[9px] text-stone-400 ml-1">{p.id.slice(0, 6)}</code>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <select defaultValue={p.categorie || "autre"} onChange={(e) => save(p, { categorie: e.target.value })} className="border border-stone-200 rounded px-2 py-1 text-xs" data-testid={`prestation-cat-${p.id}`}>
                      {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <input defaultValue={p.taux_horaire || 0} type="number" step="any" onBlur={(e) => save(p, { taux_horaire: parseFloat(e.target.value) || 0 })} className="w-20 border border-stone-200 rounded px-2 py-1 text-xs text-right" data-testid={`prestation-taux-${p.id}`}/>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{s.projets || 0}</td>
                  <td className="px-4 py-3 text-right font-mono">{(s.temps_h || 0).toFixed(1)} h</td>
                  <td className="px-4 py-3 text-right font-mono text-[var(--ppd)] font-semibold">{formatEur(s.ca_realise || 0)}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatEur(s.marge || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing(isEd ? null : p.id)} className="text-stone-500 hover:text-stone-900 mr-2" data-testid={`prestation-edit-${p.id}`}><Edit2 size={13}/></button>
                    <div className="inline-flex flex-wrap gap-1 mr-2">
                      {DEFAULT_COLORS.slice(0, 5).map((c) => (
                        <button key={c} onClick={() => save(p, { couleur: c })} className="w-3 h-3 rounded-full border border-stone-200" style={{ background: c }}/>
                      ))}
                    </div>
                    <button onClick={() => remove(p)} className="text-stone-400 hover:text-[var(--ppd)]" data-testid={`prestation-del-${p.id}`}><Trash2 size={13}/></button>
                  </td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td colSpan={mergeMode ? 9 : 8} className="text-center text-stone-400 py-12">Aucune prestation.</td></tr>}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-stone-500">
        <BarChart3 size={11} className="inline -mt-0.5"/> Analyse temps · CA · marge par prestation calculée automatiquement à partir des projets utilisant ce libellé.
      </p>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="prestation-form">
            <h3 className="font-display text-2xl font-bold mb-4">Nouvelle prestation</h3>
            <div className="space-y-3">
              <label className="flex flex-col text-sm"><span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Nom *</span><input name="nom" required className="border border-stone-200 rounded px-3 py-2"/></label>
              <label className="flex flex-col text-sm"><span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Catégorie</span>
                <select name="categorie" className="border border-stone-200 rounded px-3 py-2">{CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}</select>
              </label>
              <label className="flex flex-col text-sm"><span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Taux horaire (€)</span><input name="taux_horaire" type="number" step="any" className="border border-stone-200 rounded px-3 py-2"/></label>
              <label className="flex flex-col text-sm"><span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Couleur</span>
                <div className="flex gap-1.5 flex-wrap">
                  {DEFAULT_COLORS.map((c) => (
                    <label key={c} className="cursor-pointer">
                      <input type="radio" name="couleur" value={c} defaultChecked={c === DEFAULT_COLORS[0]} className="hidden peer"/>
                      <span className="block w-6 h-6 rounded-full border border-stone-300 peer-checked:ring-2 peer-checked:ring-[var(--ppd)]" style={{ background: c }}/>
                    </label>
                  ))}
                </div>
              </label>
              <label className="flex flex-col text-sm"><span className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mb-1">Description</span><textarea name="description" rows={2} className="border border-stone-200 rounded px-3 py-2"/></label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={() => setShow(false)} className="px-4 py-2 text-sm border border-stone-200 rounded">Annuler</button>
              <ActionButton data-testid="prestation-submit" type="button" onClick={create} successLabel="Créé" className="px-4 py-2 text-sm bg-stone-900 text-white rounded font-semibold">Créer</ActionButton>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
